import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  inject,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as d3 from 'd3';
import { ThemeService } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

export interface PolarChartData {
    label: string;
    value: number;
    color: string;
}

@Component({
    selector: 'app-polar-area-chart',
    standalone: true,
    imports: [CommonModule],
    template: `<div #chartContainer class="polar-chart-container w-full h-full relative"></div>`,
    styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
    .polar-chart-container {
      min-height: 300px;
    }
  `],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None
})
export class PolarAreaChartComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @ViewChild('chartContainer', { static: true }) chartContainer!: ElementRef;
    @Input() data: PolarChartData[] = [];
    @Input() valueType: 'number' | 'currency' = 'number';
    @Input() scaleType: 'linear' | 'logarithmic' = 'linear';
    @Input() margin = { top: 30, right: 30, bottom: 30, left: 30 };

    private themeService = inject(ThemeService);
    private resizeObserver!: ResizeObserver;
    private themeSubscription?: Subscription;
    @Input() currentTheme: 'light' | 'dark' = 'dark';

    ngOnInit() {
        this.currentTheme = this.themeService.getTheme();
    }

    ngAfterViewInit() {
        this.resizeObserver = new ResizeObserver(() => this.drawChart());
        this.resizeObserver.observe(this.chartContainer.nativeElement);

        this.themeSubscription = this.themeService.theme$.subscribe(theme => {
            this.currentTheme = theme;
            this.drawChart();
        });

        setTimeout(() => this.drawChart(), 0);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['data'] && !changes['data'].firstChange) {
            this.drawChart();
        }
    }

    ngOnDestroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.themeSubscription) this.themeSubscription.unsubscribe();
    }

    private drawChart() {
        if (!this.data || this.data.length === 0) return;

        const element = this.chartContainer.nativeElement;
        const width = element.clientWidth;
        const height = element.clientHeight || 300;

        if (width === 0) return;

        d3.select(element).selectAll('*').remove();

        const radius = Math.min(width - this.margin.left - this.margin.right, height - this.margin.top - this.margin.bottom) / 2;

        const svg = d3.select(element)
            .append('svg')
            .attr('width', '100%')
            .attr('height', '100%')
            .attr('viewBox', `0 0 ${width} ${height}`)
            .append('g')
            .attr('transform', `translate(${width / 2},${height / 2})`);

        const maxValue = d3.max(this.data, d => d.value) || 100;
        const angleStep = (Math.PI * 2) / this.data.length;

        // Radial Scale
        let rScale: d3.ScaleLinear<number, number> | d3.ScaleLogarithmic<number, number>;

        if (this.scaleType === 'logarithmic') {
            // Log scale requires values > 0. We use 1 as a base for min value.
            const domainMax = Math.max(maxValue, 1.1); // Ensure max is always > min
            rScale = d3.scaleLog()
                .range([0, radius])
                .domain([1, domainMax]);
        } else {
            rScale = d3.scaleLinear()
                .range([0, radius])
                .domain([0, maxValue]);
        }

        const themeColors = this.getThemeColors();

        // 1. Draw Grid Polygons (Background)
        const levels = 4;
        const gridGroup = svg.append('g').attr('class', 'grid-circles');

        for (let level = 1; level <= levels; level++) {
            const levelRadius = (radius / levels) * level;

            // Calculate polygon points for this level
            const levelPoints: [number, number][] = [];
            this.data.forEach((_, i) => {
                // Align points with the boundary of wedges? 
                // Standard Radar/Polar grid aligns with the axes (spokes).
                // Our spokes are at the boundaries (i * angleStep).
                // So we use those same angles.
                const angle = i * angleStep - Math.PI / 2;
                levelPoints.push([
                    levelRadius * Math.cos(angle),
                    levelRadius * Math.sin(angle)
                ]);
            });
            // Close the loop
            levelPoints.push(levelPoints[0]);

            gridGroup.append('path')
                .attr('d', d3.line()(levelPoints))
                .attr('fill', level === levels ? 'rgba(230, 230, 255, 0.02)' : 'none')
                .attr('stroke', themeColors.gridLine)
                .attr('stroke-width', 1)
                .attr('stroke-dasharray', '4,4');
        }

        // 2. Draw Spokes
        // (Existing logic is fine, straight lines from center)
        const spokes = svg.append('g').attr('class', 'spokes');
        this.data.forEach((_, i) => {
            const angle = i * angleStep - Math.PI / 2;
            const x = radius * Math.cos(angle);
            const y = radius * Math.sin(angle);

            spokes.append('line')
                .attr('x1', 0)
                .attr('y1', 0)
                .attr('x2', x)
                .attr('y2', y)
                .attr('stroke', themeColors.gridLine)
                .attr('stroke-width', 1);
        });

        // 3. Draw Wedges (The Data) with Gradients
        // Using straight path instead of arc
        const defs = svg.append('defs');

        this.data.forEach((d, i) => {
            const gradientId = `wedge-gradient-${i}`;
            const gradient = defs.append('radialGradient')
                .attr('id', gradientId)
                .attr('gradientUnits', 'userSpaceOnUse')
                .attr('cx', 0)
                .attr('cy', 0)
                .attr('r', rScale(maxValue))
                .attr('fx', 0)
                .attr('fy', 0);

            gradient.append('stop')
                .attr('offset', '0%')
                .attr('stop-color', '#ffffff')
                .attr('stop-opacity', 0.1);

            gradient.append('stop')
                .attr('offset', '100%')
                .attr('stop-color', d.color)
                .attr('stop-opacity', 0.8);
        });

        const wedgeGroup = svg.append('g').attr('class', 'wedges');

        this.data.forEach((d, i) => {
            // In log scale, values <= 1 should be treated as 1 to avoid negative radius
            const displayValue = this.scaleType === 'logarithmic' ? Math.max(d.value, 1) : d.value;
            const r = rScale(displayValue);
            const a1 = i * angleStep - Math.PI / 2;
            const a2 = (i + 1) * angleStep - Math.PI / 2;

            const p1 = [r * Math.cos(a1), r * Math.sin(a1)];
            const p2 = [r * Math.cos(a2), r * Math.sin(a2)];

            // Create a group for the wedge (Fill + Cap) to transform together
            const wedge = wedgeGroup.append('g')
                .attr('class', 'wedge-group')
                .style('cursor', 'pointer');

            // Fill Path: Center -> P1 -> P2 -> Center
            wedge.append('path')
                .attr('d', `M 0 0 L ${p1[0]} ${p1[1]} L ${p2[0]} ${p2[1]} Z`)
                .attr('fill', `url(#wedge-gradient-${i})`)
                .attr('stroke', 'none')
                .attr('class', 'wedge-fill');

            // Top Cap Line
            wedge.append('line')
                .attr('x1', p1[0])
                .attr('y1', p1[1])
                .attr('x2', p2[0])
                .attr('y2', p2[1])
                .attr('stroke', d.color)
                .attr('stroke-width', 3)
                .attr('stroke-linecap', 'round');

            // Interactions
            wedge.on('mouseenter', function () {
                const sel = d3.select(this);

                // Scale Up
                sel.transition().duration(200)
                    .attr('transform', 'scale(1.1)');

                // Show Label
                // We need to select the label corresponding to this index
                svg.select(`.value-label-${i}`)
                    .transition().duration(200)
                    .attr('opacity', 1)
                    .attr('font-size', '13px'); // Make it slightly larger
            })
                .on('mouseleave', function () {
                    const sel = d3.select(this);

                    // Scale Down
                    sel.transition().duration(200)
                        .attr('transform', 'scale(1)');

                    // Hide Label
                    svg.select(`.value-label-${i}`)
                        .transition().duration(200)
                        .attr('opacity', 0)
                        .attr('font-size', '11px');
                });
        });

        // 4. Value Labels (Hidden by default)
        const labelArc = d3.arc<PolarChartData>()
            .innerRadius(d => {
                const displayValue = this.scaleType === 'logarithmic' ? Math.max(d.value, 1) : d.value;
                return rScale(displayValue) + 15;
            })
            .outerRadius(d => {
                const displayValue = this.scaleType === 'logarithmic' ? Math.max(d.value, 1) : d.value;
                return rScale(displayValue) + 15;
            })
            .startAngle((d, i) => i * angleStep)
            .endAngle((d, i) => (i + 1) * angleStep);

        svg.selectAll('text.value-label')
            .data(this.data)
            .enter()
            .append('text')
            .attr('class', (d, i) => `value-label value-label-${i}`)
            .attr('transform', (d, i) => `translate(${labelArc.centroid(d, i)})`)
            .attr('dy', '0.35em')
            .attr('text-anchor', 'middle')
            .text(d => {
                if (this.valueType === 'currency') {
                    // Simple currency formatting for SVG text
                    return `$${d3.format(',.0f')(d.value)}`;
                }
                return d3.format(',')(d.value);
            })
            .attr('fill', themeColors.axisText)
            .style('font-size', '11px')
            .style('font-weight', 'bold')
            .attr('opacity', 0); // Hidden by default

    }

    private getThemeColors() {
        const isDark = this.currentTheme === 'dark';
        return {
            gridLine: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
            axisText: isDark ? '#e4e4e7' : '#374151'
        };
    }
}
